import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { Container } from '../theme/primitives';
import { iconMap } from '../theme/icons';
import { fititBrandRamp, stonePalette } from '../theme/brand';
import altHeroImg from '../assets/img/hero/alt-hero.jpg';
import heroWardrobe from '../assets/img/hero/hero1-new.jpg';
import heroHallway from '../assets/img/hero/hero3-new.jpg';
import heroShelves from '../assets/img/hero/hero4-new.jpg';
import heroVanity from '../assets/img/hero/hero2-new.jpg';
import heroDresser from '../assets/img/hero/hero5-new.jpg';
import cfgBannerImg from '../assets/img/marketing/cfg-banner.jpg';

const ViewInArIcon = iconMap.view_in_ar;
const ArrowForwardIcon = iconMap.arrow_forward;

// Šis puslapis yra ALTERNATYVUS titulinio dizaino PASIŪLYMAS (ne pakeitimas) —
// pasiekiamas per /pradzia-v2, tikras veikiantis maršrutas, kad būtų galima
// palyginti tiesiogiai, o ne statiniame paveikslėlyje. Struktūra sąmoningai KITOKIA
// nei Home.jsx: vietoj mozaikos+kortelių sekcijų — pilno pločio kintančios
// nuotrauka/tekstas juostos, didelė tipografija, daug tuščios erdvės (Apple.com
// stiliaus produktų puslapiai), minimaliai "kortelių" UI kalbos.
const useStyles = makeStyles({
  page: { backgroundColor: stonePalette[50] },

  // ── HERO — pilno ekrano nuotrauka, ne mozaika ────────────────────────────
  hero: { position: 'relative', minHeight: '92vh', display: 'flex', alignItems: 'flex-end', overflow: 'hidden' },
  heroImg: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' },
  heroOverlay: {
    position: 'absolute', inset: 0,
    background: 'linear-gradient(0deg, rgba(10,10,9,0.85) 0%, rgba(10,10,9,0.35) 45%, rgba(10,10,9,0.05) 70%)',
  },
  heroInner: { position: 'relative', width: '100%', paddingBottom: '5rem', paddingTop: '8rem' },
  heroEyebrow: {
    fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, textTransform: 'uppercase',
    letterSpacing: '0.12em', color: fititBrandRamp[130], marginBottom: tokens.spacingVerticalM,
  },
  heroTitle: {
    fontSize: '3rem', lineHeight: '1.05', fontWeight: tokens.fontWeightBold, color: '#ffffff',
    letterSpacing: '-0.02em', margin: 0, maxWidth: '46rem',
    '@media (min-width: 768px)': { fontSize: '4.5rem' },
    '@media (min-width: 1280px)': { fontSize: '5.5rem' },
  },
  heroSubtitle: {
    fontSize: tokens.fontSizeBase500, color: 'rgba(255,255,255,0.75)', lineHeight: '1.5',
    maxWidth: '32rem', marginTop: tokens.spacingVerticalL, marginBottom: tokens.spacingVerticalXXL,
  },
  heroActions: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalM, alignItems: 'center' },
  btnLight: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalS,
    paddingLeft: '2rem', paddingRight: '2rem', paddingTop: tokens.spacingVerticalM, paddingBottom: tokens.spacingVerticalM,
    backgroundColor: '#ffffff', color: '#0a0a09', fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase300,
    borderRadius: tokens.borderRadiusCircular, textDecorationLine: 'none', cursor: 'pointer',
    transitionProperty: 'transform, background-color', transitionDuration: tokens.durationNormal,
    ':hover': { backgroundColor: stonePalette[50] }, ':active': { transform: 'scale(0.97)' },
  },
  btnGhost: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalS,
    paddingLeft: '1.75rem', paddingRight: '1.75rem', paddingTop: tokens.spacingVerticalM, paddingBottom: tokens.spacingVerticalM,
    color: '#ffffff', fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase300,
    borderRadius: tokens.borderRadiusCircular, border: '1px solid rgba(255,255,255,0.4)', textDecorationLine: 'none', cursor: 'pointer',
    transitionProperty: 'background-color, border-color', transitionDuration: tokens.durationNormal,
    ':hover': { backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.7)' },
  },

  // ── STATS — didelis, plikas, be kortelių ─────────────────────────────────
  statsSection: { paddingTop: '5rem', paddingBottom: '5rem', backgroundColor: '#ffffff' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '2.5rem', '@media (min-width: 768px)': { gridTemplateColumns: 'repeat(4, 1fr)' } },
  statCell: { textAlign: 'center' },
  statValue: { fontSize: '2.75rem', fontWeight: tokens.fontWeightBold, color: '#0a0a09', letterSpacing: '-0.02em', lineHeight: 1 },
  statLabel: { fontSize: tokens.fontSizeBase300, color: stonePalette[500], marginTop: tokens.spacingVerticalS },

  // ── FEATURE JUOSTOS — kintanti nuotrauka/tekstas, be kortelių fono ───────
  feature: { paddingTop: '6rem', paddingBottom: '6rem' },
  featureGrid: {
    display: 'grid', gridTemplateColumns: '1fr', gap: '3rem', alignItems: 'center',
    '@media (min-width: 1024px)': { gridTemplateColumns: '1fr 1fr', gap: '5rem' },
  },
  featureImgWrap: { borderRadius: tokens.borderRadius4XLarge, overflow: 'hidden', aspectRatio: '4 / 5', boxShadow: '0 30px 60px -20px rgba(0,0,0,0.25)' },
  // DOM tvarka visada [nuotrauka, tekstas] (mobiliame — nuotrauka viršuje abiem
  // sekcijoms); `order` (ne bidi/RTL) saugiai apverčia VIZUALIĄ tvarką tik desktop'e,
  // kad sekcijos vizualiai kaitaliotųsi (nuotrauka kairėje/dešinėje).
  featureImgLast: { '@media (min-width: 1024px)': { order: 1 } },
  featureImgWrapWide: { aspectRatio: '16 / 11' },
  featureImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  featureEyebrow: {
    fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, textTransform: 'uppercase',
    letterSpacing: '0.1em', color: tokens.colorBrandForeground2, marginBottom: tokens.spacingVerticalM,
  },
  featureTitle: {
    fontSize: '2.25rem', lineHeight: '1.15', fontWeight: tokens.fontWeightBold, color: '#0a0a09',
    letterSpacing: '-0.01em', margin: 0, marginBottom: tokens.spacingVerticalL,
    '@media (min-width: 768px)': { fontSize: '2.75rem' },
  },
  featureText: { fontSize: tokens.fontSizeBase400, lineHeight: '1.65', color: stonePalette[600], marginBottom: tokens.spacingVerticalXL, maxWidth: '30rem' },
  featureLink: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS,
    fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold, color: tokens.colorBrandForegroundLink,
    textDecorationLine: 'none', ':hover': { color: tokens.colorBrandForegroundLinkHover },
  },

  // ── PILNO PLOČIO CTA JUOSTA (konfigūratorius) ────────────────────────────
  bigCta: { position: 'relative', minHeight: '34rem', display: 'flex', alignItems: 'center', overflow: 'hidden' },
  bigCtaImg: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' },
  bigCtaOverlay: { position: 'absolute', inset: 0, background: `linear-gradient(100deg, ${fititBrandRamp[10]} 0%, ${fititBrandRamp[10]} 42%, rgba(2,38,31,0.55) 60%, rgba(2,38,31,0) 88%)` },
  bigCtaInner: { position: 'relative', maxWidth: '30rem' },
  bigCtaEyebrow: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, textTransform: 'uppercase', letterSpacing: '0.1em', color: fititBrandRamp[130], marginBottom: tokens.spacingVerticalM },
  bigCtaTitle: { fontSize: '2.5rem', lineHeight: '1.15', fontWeight: tokens.fontWeightBold, color: '#ffffff', letterSpacing: '-0.01em', margin: 0, marginBottom: tokens.spacingVerticalL, '@media (min-width: 768px)': { fontSize: '3rem' } },
  bigCtaText: { fontSize: tokens.fontSizeBase400, color: 'rgba(255,255,255,0.75)', lineHeight: '1.6', marginBottom: tokens.spacingVerticalXXL },

  // ── GALERIJOS JUOSTA — 3 nuotraukos, minimalūs parašai ───────────────────
  gallerySection: { paddingTop: '6rem', paddingBottom: '6rem', backgroundColor: stonePalette[50] },
  galleryHeader: { textAlign: 'center', marginBottom: '3.5rem' },
  galleryTitle: { fontSize: '2.25rem', fontWeight: tokens.fontWeightBold, color: '#0a0a09', letterSpacing: '-0.01em', margin: 0, '@media (min-width: 768px)': { fontSize: '2.75rem' } },
  galleryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem' },
  galleryCard: { position: 'relative', borderRadius: tokens.borderRadius3XLarge, overflow: 'hidden', aspectRatio: '3 / 4' },
  galleryImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  galleryCaption: { position: 'absolute', left: '1.25rem', right: '1.25rem', bottom: '1.25rem' },
  galleryCaptionText: { color: '#ffffff', fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase300, textShadow: '0 1px 3px rgba(0,0,0,0.4)' },
  galleryOverlay: { position: 'absolute', inset: 0, background: 'linear-gradient(0deg, rgba(0,0,0,0.55) 0%, transparent 55%)' },

  // ── GALUTINIS CTA ─────────────────────────────────────────────────────────
  finalCta: { paddingTop: '7rem', paddingBottom: '7rem', textAlign: 'center', backgroundColor: '#ffffff' },
  finalCtaTitle: { fontSize: '2.5rem', fontWeight: tokens.fontWeightBold, color: '#0a0a09', letterSpacing: '-0.01em', margin: '0 auto', maxWidth: '30rem', '@media (min-width: 768px)': { fontSize: '3rem' } },
  finalCtaSub: { fontSize: tokens.fontSizeBase400, color: stonePalette[500], marginTop: tokens.spacingVerticalM, marginBottom: tokens.spacingVerticalXXL },
  finalCtaBtn: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalS,
    paddingLeft: '2.25rem', paddingRight: '2.25rem', paddingTop: tokens.spacingVerticalM, paddingBottom: tokens.spacingVerticalM,
    backgroundColor: tokens.colorBrandBackground, color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400, borderRadius: tokens.borderRadiusCircular,
    textDecorationLine: 'none', cursor: 'pointer', transitionProperty: 'background-color, transform', transitionDuration: tokens.durationNormal,
    ':hover': { backgroundColor: tokens.colorBrandBackgroundHover }, ':active': { transform: 'scale(0.97)' },
  },

  sectionPadX: { paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL, '@media (min-width: 1024px)': { paddingLeft: '4rem', paddingRight: '4rem' } },
});

function HomeAlt() {
  const s = useStyles();
  const { t } = useTranslation();
  useEffect(() => { document.title = t('home_alt.doc_title'); }, [t]);

  return (
    <div className={s.page}>

      {/* ─── HERO — pilnas ekranas, viena nuotrauka ─────────────────────── */}
      <section className={s.hero}>
        <img src={altHeroImg} alt={t('home_alt.hero_img_alt')} className={s.heroImg} />
        <div className={s.heroOverlay} />
        <Container size="xl" className={mergeClasses(s.heroInner, s.sectionPadX)}>
          <div className={s.heroEyebrow}>{t('home_alt.hero_eyebrow')}</div>
          <h1 className={s.heroTitle}>{t('home_alt.hero_title_1')}<br />{t('home_alt.hero_title_2')}</h1>
          <p className={s.heroSubtitle}>
            {t('home_alt.hero_subtitle')}
          </p>
          <div className={s.heroActions}>
            <Link to="/konfiguratorius" className={s.btnLight}>
              <ViewInArIcon fontSize={18} />
              {t('home_alt.hero_cta_start')}
            </Link>
            <Link to="/baldai" className={s.btnGhost}>
              {t('home_alt.hero_cta_catalog')}
            </Link>
          </div>
        </Container>
      </section>

      {/* ─── STATS ────────────────────────────────────────────────────────── */}
      <section className={mergeClasses(s.statsSection, s.sectionPadX)}>
        <Container size="xl">
          <div className={s.statsGrid}>
            {[
              { value: '1 000+', labelKey: 'home_alt.stat_clients' },
              { value: '10+', labelKey: 'home_alt.stat_experience' },
              { value: '4–6 sav.', labelKey: 'home_alt.stat_production' },
              { value: '2 metai', labelKey: 'home_alt.stat_warranty' },
            ].map(({ value, labelKey }) => (
              <div key={labelKey} className={s.statCell}>
                <div className={s.statValue}>{value}</div>
                <div className={s.statLabel}>{t(labelKey)}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ─── FEATURE 1 — nuotrauka kairėje ───────────────────────────────── */}
      <section className={mergeClasses(s.feature, s.sectionPadX)}>
        <Container size="xl">
          <div className={s.featureGrid}>
            <div className={s.featureImgWrap}>
              <img src={heroHallway} alt={t('home_alt.feature1_img_alt')} className={s.featureImg} />
            </div>
            <div>
              <div className={s.featureEyebrow}>{t('home_alt.feature1_eyebrow')}</div>
              <h2 className={s.featureTitle}>{t('home_alt.feature1_title')}</h2>
              <p className={s.featureText}>
                {t('home_alt.feature1_text')}
              </p>
              <Link to="/konfiguratorius" className={s.featureLink}>
                {t('home_alt.feature1_link')} <ArrowForwardIcon fontSize={16} />
              </Link>
            </div>
          </div>
        </Container>
      </section>

      {/* ─── FEATURE 2 — nuotrauka dešinėje ──────────────────────────────── */}
      <section className={mergeClasses(s.feature, s.sectionPadX)} style={{ backgroundColor: stonePalette[50] }}>
        <Container size="xl">
          <div className={s.featureGrid}>
            <div className={mergeClasses(s.featureImgWrap, s.featureImgLast)}>
              <img src={heroShelves} alt={t('home_alt.feature2_img_alt')} className={s.featureImg} />
            </div>
            <div>
              <div className={s.featureEyebrow}>{t('home_alt.feature2_eyebrow')}</div>
              <h2 className={s.featureTitle}>{t('home_alt.feature2_title')}</h2>
              <p className={s.featureText}>
                {t('home_alt.feature2_text')}
              </p>
              <Link to="/baldai" className={s.featureLink}>
                {t('home_alt.feature2_link')} <ArrowForwardIcon fontSize={16} />
              </Link>
            </div>
          </div>
        </Container>
      </section>

      {/* ─── PILNO PLOČIO CTA ─────────────────────────────────────────────── */}
      <section className={mergeClasses(s.bigCta, s.sectionPadX)}>
        <img src={cfgBannerImg} alt={t('home_alt.big_cta_img_alt')} className={s.bigCtaImg} />
        <div className={s.bigCtaOverlay} />
        <Container size="xl" className={s.bigCtaInner}>
          <div className={s.bigCtaEyebrow}>{t('home_alt.big_cta_eyebrow')}</div>
          <h2 className={s.bigCtaTitle}>{t('home_alt.big_cta_title')}</h2>
          <p className={s.bigCtaText}>{t('home_alt.big_cta_text')}</p>
          <Link to="/konfiguratorius" className={s.btnLight}>
            <ViewInArIcon fontSize={18} />
            {t('home_alt.big_cta_btn')}
          </Link>
        </Container>
      </section>

      {/* ─── GALERIJA ─────────────────────────────────────────────────────── */}
      <section className={mergeClasses(s.gallerySection, s.sectionPadX)}>
        <Container size="xl">
          <div className={s.galleryHeader}>
            <h2 className={s.galleryTitle}>{t('home_alt.gallery_title')}</h2>
          </div>
          <div className={s.galleryGrid}>
            {[
              { img: heroWardrobe, labelKey: 'home_alt.gallery_item_0' },
              { img: heroVanity, labelKey: 'home_alt.gallery_item_1' },
              { img: heroDresser, labelKey: 'home_alt.gallery_item_2' },
            ].map(({ img, labelKey }) => (
              <div key={labelKey} className={s.galleryCard}>
                <img src={img} alt={t(labelKey)} className={s.galleryImg} />
                <div className={s.galleryOverlay} />
                <div className={s.galleryCaption}>
                  <span className={s.galleryCaptionText}>{t(labelKey)}</span>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ─── GALUTINIS CTA ────────────────────────────────────────────────── */}
      <section className={mergeClasses(s.finalCta, s.sectionPadX)}>
        <Container size="md">
          <h2 className={s.finalCtaTitle}>{t('home_alt.final_cta_title')}</h2>
          <p className={s.finalCtaSub}>{t('home_alt.final_cta_sub')}</p>
          <Link to="/konfiguratorius" className={s.finalCtaBtn}>
            <ViewInArIcon fontSize={18} />
            {t('home_alt.final_cta_btn')}
          </Link>
        </Container>
      </section>

    </div>
  );
}

export default HomeAlt;
